import { gql, useApolloClient } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import aryApiTypes from '../constants/aryApiTypes';
import {
  GET_DRAFT_CURRENCIES,
  GET_USER_CURRENCIES
} from '../constants/aryGraphqlSchemas';
import { GET_CURRENCIES } from '../constants/aryRestCommands';
import useAryGetData from '../hooks/useAryGetData';
import AryAutoCompleteBase from './AryAutoCompleteBase';

function AryCurrencyAutoComplete({
  value,
  onChange,
  hasAllOption,
  handleChangeDecimalPlaces,
  excludedCurrencyCodes,
  getMirrorCurrencies = false,
  branchId,
  onlyCurrencyCodes,
  apiType = aryApiTypes.graphql,
  handleChangeCurrencyCounts,
  disabled,
  style,
  ...others
}) {
  const { t } = useFrameworkTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const client = useApolloClient();

  const [getCurrencies] = useAryGetData(GET_CURRENCIES);

  // if (apiType === aryApiTypes.graphql) {
  //   client = useApolloClient();
  // }
  async function loadOptionsGraphql() {
    setLoading(true);
    const queryName = getMirrorCurrencies
      ? GET_DRAFT_CURRENCIES
      : GET_USER_CURRENCIES;
    const r = await client.query({
      query: gql`
        ${queryName.gqlSchema(`
          code
          title
          decimalPlaces
      `)}
      `,
      variables: getMirrorCurrencies ? { branchId } : undefined,
      fetchPolicy: 'no-cache'
    });
    const responseData = getMirrorCurrencies
      ? r?.data?.branchManagementQueries.getDraftCurrencies
      : r?.data?.systemManagementQueries.getUserCurrencies;
    const finalOptions = responseData?.map((currency) => ({
      value: currency.code,
      label: currency.title,
      decimalPlaces: currency.decimalPlaces
    }));
    if (handleChangeCurrencyCounts) {
      handleChangeCurrencyCounts(finalOptions.length);
    }
    const allOption = hasAllOption
      ? [{ value: 0, label: t('AllCurrencies') }]
      : [];
    const newOptions = allOption.concat(finalOptions);
    setOptions(newOptions);
    setLoading(false);
  }

  async function loadOptionsRest() {
    setLoading(true);
    const data = await getCurrencies();

    const finalOptions = Array.isArray(data)
      ? data?.map((currency) => ({
          value: currency.code,
          label: currency.title,
          decimalPlaces: currency.decimalPlaces
        }))
      : [];
    const allOption = hasAllOption
      ? [{ value: 0, label: t('AllCurrencies') }]
      : [];
    const newOptions = allOption.concat(finalOptions);
    setOptions(newOptions);
    setLoading(false);
  }

  async function loadOptions() {
    if (apiType === aryApiTypes.graphql) {
      await loadOptionsGraphql();
    } else if (apiType === aryApiTypes.rest) {
      await loadOptionsRest();
    }
  }

  useEffect(() => {
    if (value && handleChangeDecimalPlaces) {
      handleChangeDecimalPlaces(
        // if the result is less than 0 then it will return 0
        options?.find((obj) => obj.value === value)?.decimalPlaces < 0
          ? 0
          : options?.find((obj) => obj.value === value)?.decimalPlaces
      );
    }
  }, [value, options]);

  useEffect(() => {
    loadOptions();
  }, [getMirrorCurrencies]);

  return (
    <AryAutoCompleteBase
      options={options}
      loading={loading}
      style={style}
      disabledSearchText
      disabled={disabled}
      value={
        value || value === 0 ? options?.find((obj) => obj.value === value) : ''
      }
      onChange={(selectedObj) => {
        onChange(selectedObj);
      }}
      filterOption={(option, inputValue) => {
        if (excludedCurrencyCodes?.length) {
          return (
            excludedCurrencyCodes.indexOf(option.value) === -1 &&
            option.value !== -1 &&
            option.label.includes(inputValue)
          );
        }
        if (onlyCurrencyCodes?.length) {
          return (
            onlyCurrencyCodes.indexOf(option.value) > -1 &&
            option.value !== -1 &&
            option.label.includes(inputValue)
          );
        }
        return option.value !== -1 && option.label.includes(inputValue);
      }}
      loadOptions={loadOptions}
      {...others}
    />
  );
}

export default AryCurrencyAutoComplete;
